import { createContext, useContext, useState, useCallback } from "react";
import EmailComposer from "./EmailComposer";

interface ComposerState {
  isOpen: boolean;
  isMinimized: boolean;
  defaultTo?: string;
  defaultContactId?: string;
  defaultSubject?: string;
}

interface EmailComposerContextType {
  openComposer: (options?: { to?: string; contactId?: string; subject?: string }) => void;
  closeComposer: () => void;
  minimizeComposer: () => void;
  maximizeComposer: () => void;
}

const EmailComposerContext = createContext<EmailComposerContextType | undefined>(undefined);

export function EmailComposerProvider({ children }: { children: React.ReactNode }) {
  const [composerState, setComposerState] = useState<ComposerState>({
    isOpen: false,
    isMinimized: false,
  });

  const openComposer = useCallback((options?: { to?: string; contactId?: string; subject?: string }) => {
    setComposerState({
      isOpen: true,
      isMinimized: false,
      defaultTo: options?.to,
      defaultContactId: options?.contactId,
      defaultSubject: options?.subject,
    });
  }, []);

  const closeComposer = useCallback(() => {
    setComposerState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const minimizeComposer = useCallback(() => {
    setComposerState((prev) => ({ ...prev, isMinimized: true }));
  }, []);

  const maximizeComposer = useCallback(() => {
    setComposerState((prev) => ({ ...prev, isMinimized: false }));
  }, []);

  return (
    <EmailComposerContext.Provider
      value={{
        openComposer,
        closeComposer,
        minimizeComposer,
        maximizeComposer,
      }}
    >
      {children}
      {composerState.isOpen && (
        <EmailComposer
          onClose={closeComposer}
          onMinimize={composerState.isMinimized ? maximizeComposer : minimizeComposer}
          isMinimized={composerState.isMinimized}
          defaultTo={composerState.defaultTo}
          defaultContactId={composerState.defaultContactId}
          defaultSubject={composerState.defaultSubject}
        />
      )}
    </EmailComposerContext.Provider>
  );
}

export function useEmailComposer() {
  const context = useContext(EmailComposerContext);
  if (context === undefined) {
    throw new Error("useEmailComposer must be used within an EmailComposerProvider");
  }
  return context;
}